import ArrowForwardIcon from '@edna/icons/arrowForward.svg';
import {TCurrency} from '@edna/utils';

import {TBadgeColor} from 'src/components';
import {TListPayload} from 'src/components/List';
import {EChannelType, EOperatorName} from 'src/constants';
import {EDocumentType, EResellerIds} from 'src/containers/User';
import {DocumentationIcon, PhoneDeviceIcon} from 'src/icons';

enum ETariffStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  CLOSED = 'CLOSED',
}

type TTariffChannelType = Exclude<EChannelType, 'PUSH' | 'VK_NOTIFY' | 'OK_NOTIFY'>;

type TTariff = {
  id: number;
  name: string;
  resellerId: EResellerIds;
  type: TTariffChannelType;
  status: ETariffStatus;
  documentType: EDocumentType;
  isSmsCommon?: boolean;
};

enum ETariffDetailType {
  LICENSE = 'LICENSE',
  OPERATOR = 'OPERATOR',
  REGION = 'REGION',
}

enum ETariffDetailStatus {
  ACTIVE = 'ACTIVE',
  PLANNED = 'PLANNED',
}

type TTariffDetailType = keyof typeof ETariffDetailType;

type TTariffDetail = {
  id: number;
  label: EOperatorName | string;
  type: TTariffDetailType;
  activeFrom: string;
  activeTo?: string;
  country: string;
  currency: TCurrency;
  version: number;
  active: boolean;
  planned: boolean;
  data?: {
    periodMonths?: number;
    mauCount?: number;
  };
  plannedDetail?: TTariffDetail;
  archivedDetails?: TTariffDetail[];
};

enum ESmsPricingType {
  FEE = 'fee',
  MULTI = 'multi',
  COMMON = 'common',
  MAU = 'mau',
  MULTI_MAU = 'multimau',
  /** @deprecated */
  FEE_FIX = 'fee-fix',
}

enum ESmsTrafficType {
  AUTH = 'auth',
  SERVICE = 'serv',
  INFO = 'info',
  MARKETING = 'mrkt',
  ADV = 'adv',
}

enum EViberTrafficType {
  SERVICE = 'service',
  MARKETING = 'marketing',
}

enum EWhatsappTrafficType {
  SERVICE = 'service',
  UTILITY = 'utility',
  AUTHENTICATION = 'authentication',
  MARKETING_LITE = 'marketing_lite',
  MARKETING = 'marketing',
}

enum ELicenseLabelType {
  LICENSE = 'license',
  CONNECTION_FEE = 'connection-fee',
  AMAU = 'amau',
  PACKAGE = 'package',
}

type TSmsPricingStep = {
  order: number;
  lowerLimit: number;
  upperLimit?: number | null;
  trafficTypes: {
    label: ESmsTrafficType;
    price: number;
  }[];
};

type TSmsMauPackage = {
  order: number;
  lowerLimit: number;
  upperLimit?: number | null;
  amount: number;
  perMessage: boolean;
};

type TSmsMauPricing = {
  label: ESmsTrafficType;
  packages: TSmsMauPackage[];
};

type TSmsPricing = {
  recurrentAmount?: number;
  templateAmount?: number;
  steps?: TSmsPricingStep[];
  mau?: TSmsMauPricing[];
};

type ReplaceAll<
  S extends string,
  From extends string,
  To extends string,
> = S extends `${infer Left}${From}${infer Right}`
  ? `${Left}${To}${ReplaceAll<Right, From, To>}`
  : S;

type TTariffType =
  | ReplaceAll<`${TTariffChannelType}-${ETariffDetailType}`, '_', '-'>
  | 'SMS-OPERATOR-MAU';

type TTariffPricingDetail = {
  id: number;
  label: ELicenseLabelType | ESmsPricingType | EWhatsappTrafficType | EViberTrafficType;
  type: TTariffType;
  activeFrom: string;
  activeTo?: string;
  version: number;
  active: boolean;
  planned: boolean;
  data: {price?: number} & TSmsPricing;
  plannedDetail?: TTariffPricingDetail;
  archivedDetails?: TTariffPricingDetail[];
};

type TDetailFormData = Partial<
  Omit<TTariffDetail | TTariffPricingDetail, 'activeFrom' | 'data' | 'activeTo'>
> & {
  activeFromDate?: Date | null;
  activeFromTime: string;
  data: string;
  activeTo?: string | null;
};

type TTariffDetailFullInfo = TTariffDetail & {
  pricingDetails: (TTariffPricingDetail & {tariffDetailId: number})[];
  catalogTariffId: number;
};

type TTariffFullInfo = TTariff & {
  details: TTariffDetailFullInfo[];
};

type TTariffDetailsGroupedSearchInfo = TTariffDetailFullInfo & {
  plannedDetail?: TTariffDetailFullInfo;
  archivedDetails?: TTariffDetailFullInfo[];
};

const statusColorHash: Record<ETariffStatus, TBadgeColor> = {
  ACTIVE: 'GREEN',
  ARCHIVED: 'YELLOW',
  CLOSED: 'GREY',
};

const tariffDetailIconHash = {
  [ETariffDetailType.LICENSE]: DocumentationIcon,
  [ETariffDetailType.REGION]: ArrowForwardIcon,
  [ETariffDetailType.OPERATOR]: PhoneDeviceIcon,
};

type TBaseTariffFilters = {
  detailTypes?: TTariffDetail['type'][];
  countries?: TTariffDetail['country'][];
};

type TTariffFilters = TBaseTariffFilters | (TBaseTariffFilters & Record<string, string[]>);

type TCatalogTariffsFilters = {
  sort?: TListPayload['sort'];
  filter?: string;
};

type TTariffCopyPayload = Pick<TTariff, 'documentType' | 'resellerId'> & {
  originalTariffId: number;
  newName: string;
  activeFromDate?: Date;
  activeFromTime: string;
};

const POLLING_INTERVAL = 60_000;
const DEFAULT_ACTIVATION_TIME = '00:00';
const MOSCOW_TIMEZONE_OFFSET = '+03:00';
const UNDEFINED_COUNTRY = 'UNDEFINED' as const;
const TARIFF_CHANNEL_TYPES = [
  EChannelType.WHATSAPP,
  EChannelType.SMS,
  EChannelType.VIBER,
  EChannelType.MAX_BOT,
  EChannelType.MAX,
  EChannelType.TELEGRAM,
];

export {
  ETariffDetailType,
  ETariffStatus,
  ETariffDetailStatus,
  statusColorHash,
  tariffDetailIconHash,
  POLLING_INTERVAL,
  DEFAULT_ACTIVATION_TIME,
  MOSCOW_TIMEZONE_OFFSET,
  UNDEFINED_COUNTRY,
  TARIFF_CHANNEL_TYPES,
  ELicenseLabelType,
  EWhatsappTrafficType,
  EViberTrafficType,
  ESmsTrafficType,
  ESmsPricingType,
};
export type {
  TTariff,
  TTariffDetail,
  TTariffPricingDetail,
  TTariffDetailType,
  TTariffFilters,
  TTariffFullInfo,
  TTariffDetailFullInfo,
  TTariffChannelType,
  TDetailFormData,
  TTariffCopyPayload,
  TSmsPricingStep,
  TCatalogTariffsFilters,
  TTariffDetailsGroupedSearchInfo,
  TSmsPricing,
  TSmsMauPricing,
  TSmsMauPackage,
};
